'use client';

import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { BlockComponentModel } from '@/cms/types/blok-component';
import { SliceHeadingStrapi } from '@/cms/types/block';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/components/atoms/Text';
import { Accordion } from '@/components/molecules/accordion';
import {
  AccordionListComponent,
  DEFAULT_ACCORDION_TIMER_DURATION_SEC,
} from '@/components/molecules/accordion/AccordionList';
import { useAccordion } from '@/components/molecules/accordion/AccordionProvider';
import WithHasUserInteracted from '@/components/organisms/VideoAccordion/WithHasUserInteracted';

export interface ImageAccordionsStrapiProps {
  image: ImageStrapiProps;
  header?: BlockHeader;
  accordions: {
    title: string;
    titleTag?: 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    description: RichTextFieldStrapiProps;
    button?: LinkButtonStrapiProps;
    image?: ImageStrapiProps;
  }[];
  backgroundVariant?: 'none' | 'grey' | 'gradient';
  sliceHeading?: SliceHeadingStrapi;
}

function ImageAccordionsStrapiComponent({
  block,
  ...rest
}: BlockComponentModel<ImageAccordionsStrapiProps>) {
  const {
    header,
    accordions,
    image: mainImage,
    backgroundVariant,
    sliceHeading,
  } = block;
  const hasSliceHeaderTag = Boolean(sliceHeading?.data?.attributes?.tag);
  const { openItemId, setOpenItemId } = useAccordion();

  const handleAccordionClick = (id: string) => {
    setOpenItemId(id);
  };

  const imageToRender = accordions[Number(openItemId)]?.image ?? mainImage;

  const [displayedImage, setDisplayedImage] = useState(imageToRender);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (imageToRender !== displayedImage) {
      setIsFadingOut(true);

      const timeout = setTimeout(() => {
        setDisplayedImage(imageToRender);
        setIsFadingOut(false);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [imageToRender, displayedImage]);

  let backgroundVariantOptions = '';
  if (backgroundVariant === 'gradient') {
    backgroundVariantOptions = 'bg-main-light-gradient';
  } else if (backgroundVariant === 'grey') {
    backgroundVariantOptions = 'bg-gray-100';
  }
  const formattedAccordionList =
    accordions?.map((item) => ({
      header: (
        <Text
          as={item.titleTag}
          variant='subtitle-l-d'
          onClick={() => handleAccordionClick(item.toString())}
          classNames={{ wrapper: 'font-serif' }}
        >
          {item.title}
        </Text>
      ),
      children: (
        <div className='flex flex-col gap-6'>
          <div
            className={cn('rounded p-8 md:hidden', backgroundVariantOptions)}
          >
            <ImageStrapi
              key={openItemId}
              block={imageToRender}
              inner={{
                classNames: {
                  image: 'rounded',
                },
              }}
            />
          </div>
          <RichTextFieldStrapi block={item.description} />
          {item?.button && (
            <LinkButtonStrapi
              block={item.button}
              inner={{ classNames: { root: 'w-full md:w-fit' } }}
            />
          )}
        </div>
      ),
    })) || [];

  return (
    <BlockWrapper
      {...rest}
      data-tracker-section={SECTION_NAMES.IMAGE_ACCORDIONS}
      classNames={{
        root: cn(hasSliceHeaderTag && 'px-9 pt-0 md:px-0 md:pt-0'),
      }}
    >
      <div
        className={cn(
          'relative flex w-full items-start gap-8 overflow-hidden',
          hasSliceHeaderTag && 'md:px-10'
        )}
      >
        <div className='min-h-[580px] w-full min-w-0 max-w-[440px] md:w-1/2 md:max-w-[648px] md:shrink-0'>
          <BlockHeaderContent
            {...header}
            alignment='left'
            classNames={{ header: '!mb-12' }}
          >
            <WithHasUserInteracted>
              <AccordionListComponent
                accordionList={formattedAccordionList}
                internal={{
                  timer: {
                    durationSec: DEFAULT_ACCORDION_TIMER_DURATION_SEC,
                  },
                }}
              />
            </WithHasUserInteracted>
          </BlockHeaderContent>
        </div>
        <div
          className={cn(
            backgroundVariantOptions,
            'hidden min-w-0 flex-1 md:flex md:items-center md:justify-center md:rounded-l md:text-center 2xl:rounded',
            'max-w-[720px] -mt-20 overflow-hidden pb-20 pt-20'
          )}
        >
          <div
            className={cn(
              'relative w-full max-w-[540px] overflow-hidden transition-opacity duration-300 ease-in-out',
              isFadingOut ? 'opacity-0' : 'opacity-100'
            )}
          >
            <ImageStrapi
              block={displayedImage}
              inner={{
                classNames: {
                  image:
                    'rounded h-auto w-full max-h-[460px] object-contain',
                },
              }}
            />
          </div>
        </div>
      </div>
    </BlockWrapper>
  );
}

export default function ImageAccordionsStrapi(
  props: BlockComponentModel<ImageAccordionsStrapiProps>
) {
  return (
    <Accordion>
      <ImageAccordionsStrapiComponent {...props} />
    </Accordion>
  );
}
